import { Stack, Typography } from '@mui/material'
import { ArrowRightIcon } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'

import { Button, Card } from '@repo/ui'

import { Activity, type ActivityRow } from 'components/display/Activity/Activity'
import { toCertificateActivityRow } from 'components/display/Activity/certificateActivityRows'
import { useAuth } from 'hooks'
import { useCompanyCertificateActivity } from 'hooks/api'
import { ROUTES } from 'routes'

const FEED_LIMIT = 6

/**
 * Recent activity — what happened to this company's certificates (VRG-338).
 *
 * THE BLOCK THAT EARNS A PERMANENT HOME. The checklist and tours are scaffolding and retire;
 * the orientation cards duplicate the sidebar. This is the only block telling the viewer
 * something they did not already know, and "what happened since I was last here" has no other
 * home — Analytics answers "how much, over time", a different question.
 *
 * Renders through the SHARED `Activity` timeline, the same component behind Settings →
 * Activity Log, so the two read as one product feature rather than two takes on a feed. Its
 * icon prop exists for exactly this: "keeps the shared component generic so the company log
 * and certificate activity stay in parity".
 *
 * DIFFERENT DATA from that page, though. The audit log records who changed the CONFIGURATION
 * (roles, users, payment methods) — a compliance question. This records what happened to the
 * PRODUCTS, including by customers and anonymous visitors who never appear in an audit log.
 * That is also why `ActivityRow.user` had to become optional: most rows here have no account
 * behind them.
 */
export const RecentActivity = () => {
  const { profile } = useAuth()
  const { data, isLoading } = useCompanyCertificateActivity({ limit: FEED_LIMIT })

  const items = data?.data ?? []

  const rows: ActivityRow[] = items.map((item) => toCertificateActivityRow(item, profile?.id))

  // No block at all when there is nothing to show. An empty "Recent activity" card on a new
  // brand's Home would take the best space on the page to report that nothing has happened,
  // which the user can already see. Suppressed only once loading has settled, so the block
  // does not flash out from under a skeleton.
  if (!isLoading && !rows.length) return null

  return (
    <Card spacing={4}>
      {/*
        THE "VIEW ALL" LINK, and why it took three asks to build.

        Twice this card shipped without one, for a reason that was correct both times: the
        obvious targets were the wrong KIND of thing. "All certificates" went to the certificates
        LIST, not a feed of EVENTS. Personal -> Activity is the admin AUDIT LOG (company_updated,
        user_added, permissions_updated) filtered to you — not this card's certificate events
        (scans, transfers, issuance). Either link would have handed someone a page that looked
        related and contained none of the rows they clicked to see more of.

        The answer was never "no link", it was that the right destination did not exist. It does
        now: `/certificates/activity` is this exact feed at full length, same hook family, same
        shared timeline. So the button goes to that.

        The label is "View all", not "View activity": the card's own heading two inches to the
        left already says "Recent activity", so naming the noun again made the button repeat the
        title rather than state what pressing it does. "All" is the only word that carries new
        information here — it is the SAME feed without the six-row cap.
      */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={4}>
        <Typography variant="h6">Recent activity</Typography>
        <Button
          component={RouterLink}
          to={ROUTES.COMPANY_CERTIFICATE_ACTIVITY}
          variant="tertiary"
          // Default size (40px), NOT `small`. A card's action is a real action and reads as
          // one at the same height every other button on a settings page uses; shrinking it
          // makes it look like a caption that happens to be clickable.
          endIcon={<ArrowRightIcon size={16} strokeWidth={1.5} />}
        >
          {/* "View all", not "View activity" (VRG-472, user directive). The card's own heading
              two inches to the left already says "Recent activity", so the button only has to
              name the RELATION to it — all of them, rather than these six. "View all activity"
              was offered as the self-describing alternative and not taken. */}
          View all
        </Button>
      </Stack>

      {/* `showActionLine={false}`: the shared component's search / sort / filter toolbar
          belongs on the full Activity page, not on six rows of a home page.

          `actor="name"`: every row here says WHO ("You", "An owner", "Someone") but has no
          avatar behind it — most of this feed is customers and anonymous scanners with no
          account — so the circle rendered generated initials, a coloured disc carrying no
          information, once per row, right next to the category icon that carries plenty. */}
      <Activity activity={rows} loading={isLoading} showActionLine={false} actor="name" />
    </Card>
  )
}
